import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";

/**
 * VisitFlowRail — sticky compact vertical navigator on the visit page.
 * Shows the clinical flow (Vitals → Complaints → Diagnosis → Investigations
 * → Rx → Advice → Save) as numbered dots with an icon. Clicking a step
 * scrolls to that section (via anchor id). "filled" dots turn pink; the
 * currently visible dot gets a soft ring.
 *
 * Props:
 *   steps — [{ id, label, icon, filled }] in clinical order
 */
export default function VisitFlowRail({ steps = [] }) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!steps.length) return;
    // Observe each section — mark the one intersecting the viewport as active.
    const els = steps
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the viewport's top that IS intersecting.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [steps]);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      aria-label="Visit flow"
      className="hidden lg:flex sticky top-24 flex-col gap-1 pr-2 py-2 self-start"
      data-testid="visit-flow-rail"
    >
      {steps.map((s, i) => {
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => jump(s.id)}
            className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full text-left transition-colors ${
              isActive
                ? "bg-pink-50 border border-pink-300 text-pink-800"
                : s.filled
                ? "bg-white border border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
            }`}
            data-testid={`flow-step-${s.id}`}
            title={s.label}
          >
            <span
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                isActive
                  ? "bg-pink-600 text-white"
                  : s.filled
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {s.filled ? <Check size={10} strokeWidth={3} /> : i + 1}
            </span>
            <span className="hidden xl:inline text-[11px] font-medium truncate max-w-[140px]">{s.label}</span>
            {s.icon && <span className="xl:hidden">{s.icon}</span>}
          </button>
        );
      })}
    </nav>
  );
}
